/**
 * <LoginPage> — se connecter (page).
 *
 * L'écran `/login` : l'identifiant d'abord, puis son mot de passe ou son code. Une seule page et
 * non trois, parce que c'est un seul écran dans l'app — la flèche de retour remonte d'une étape
 * avant de quitter l'écran, et le code s'ouvre depuis l'une ou l'autre des deux premières étapes.
 *
 * Contrôlée : l'étape arrive en prop avec ses données, et chaque état s'ouvre seul dans une story.
 *
 * | Étape                 | Ce qu'on passe                                                    |
 * |-----------------------|-------------------------------------------------------------------|
 * | `start`               | le champ e-mail ou numéro (`LoginStartStepProps`)                 |
 * | `password`            | le mot de passe d'une adresse connue (`LoginPasswordStepProps`)   |
 * | `code`                | la saisie du code (`LoginCodeStepProps`)                          |
 * | `connectedElsewhere`  | rien : la connexion s'est faite dans un autre onglet (web)        |
 */
import React from "react"

import { Stack } from "../../atoms/containers/Stack"
import { WaitingText } from "../../atoms/WaitingText"
import { AuthHeading } from "../../molecules/AuthHeading"
import { LoginCodeStep, type LoginCodeStepProps } from "../../organisms/LoginCodeStep"
import { LoginPasswordStep, type LoginPasswordStepProps } from "../../organisms/LoginPasswordStep"
import { LoginStartStep, type LoginStartStepProps } from "../../organisms/LoginStartStep"
import { AuthTemplate } from "../../templates/AuthTemplate"

export type LoginPageStep =
    | ({ step: "start" } & LoginStartStepProps)
    | ({ step: "password" } & LoginPasswordStepProps)
    | ({ step: "code" } & LoginCodeStepProps)
    | { step: "connectedElsewhere" }

export type LoginPageProps = {
    /** L'étape montrée, avec ses données. */
    content: LoginPageStep
    /**
     * La flèche de retour : d'une étape à la précédente, puis hors de l'écran — c'est le view-model
     * qui sait. Absente → pas de flèche (l'arrivée depuis une inscription, sur le mot de passe).
     */
    onBack?: () => void
    testID?: string
}

export function LoginPage(props: LoginPageProps) {
    const { content, onBack, testID } = props

    return (
        // La première étape est l'accueil du livre (lueur, logo en grand). Les suivantes sont des
        // formulaires, où le livre interdit la lueur.
        <AuthTemplate onBack={onBack} welcome={content.step === "start"} testID={testID}>
            {content.step === "start" && <LoginStartStep {...content} />}
            {content.step === "password" && <LoginPasswordStep {...content} />}
            {content.step === "code" && <LoginCodeStep {...content} />}
            {content.step === "connectedElsewhere" && (
                <Stack gap="6" align="center">
                    <AuthHeading title="Vous êtes connecté" />
                    {/* L'écran suivant arrive seul : la phrase se souligne en boucle le temps qu'il vienne,
                        et c'est elle qui s'annonce au lecteur d'écran. */}
                    <WaitingText numberOfLines={2}>La connexion s'est faite dans un autre onglet.</WaitingText>
                </Stack>
            )}
        </AuthTemplate>
    )
}
